import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Users,
  Brain,
  Target,
  Star,
  CheckCircle,
  ArrowRight,
  TrendingUp,
  Zap,
  Award,
  Sparkles
} from 'lucide-react';

const Home = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Brain className="w-8 h-8" />,
      title: "Matching Intelligent",
      description: "Système de correspondance alimenté par l'IA qui connecte les bons candidats aux opportunités parfaites",
      benefits: [
        "Annonces d'emploi illimitées",
        "Accès aux coordonnées des candidats",
        "Générateur d'annonces IA",
        "Algorithme de matching intelligent",
        "Crédits base de données CV"
      ],
      color: "from-yellow-500 to-yellow-600"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Sourcing de Talents",
      description: "Sourcing complet 360° pour trouver les meilleurs candidats pour votre organisation",
      benefits: [
        "Compréhension du profil",
        "Sourcing 360°",
        "Planification d'entretiens",
        "Sélection en 10 jours",
        "Assurance qualité"
      ],
      color: "from-blue-500 to-blue-600"
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Marque Employeur",
      description: "Mettez en valeur votre culture d'entreprise et attirez les meilleurs talents avec une visibilité renforcée",
      benefits: [
        "Mise en avant entreprise",
        "Photos et vidéos",
        "En ligne en 5 jours",
        "Campagnes de communication",
        "Amélioration de l'image"
      ],
      color: "from-green-500 to-green-600"
    }
  ];

  const testimonials = [
    {
      name: "Ahmed Bensaid",
      company: "TechCorp Algérie",
      role: "Directeur RH",
      content: "Tekhdem a révolutionné notre processus de recrutement. Le matching alimenté par l'IA nous a fait économiser des semaines de tri et nous a aidés à trouver des talents exceptionnels.",
      rating: 5
    },
    {
      name: "Fatima Larbi",
      company: "StartupDZ",
      role: "PDG",
      content: "Le processus de sélection numérique via Tekhdem nous a permis de recruter efficacement des profils de haut niveau. La plateforme a rationalisé tout notre flux de travail RH.",
      rating: 5
    },
    {
      name: "Karim Mendili",
      company: "InnovateDZ",
      role: "Responsable Recrutement",
      content: "L'équipe de Tekhdem est professionnelle, dynamique et réactive. Leur plateforme a facilité la recherche de candidats de qualité dans notre domaine spécialisé.",
      rating: 5
    }
  ];

  const stats = [
    { number: "1+", label: "Années d'Expérience", icon: <Award className="w-6 h-6" /> },
    { number: "10+", label: "Entreprises Partenaires", icon: <Users className="w-6 h-6" /> },
    { number: "1k+", label: "Membres Actifs", icon: <TrendingUp className="w-6 h-6" /> },
    { number: "100+", label: "Collaborateurs", icon: <Sparkles className="w-6 h-6" /> }
  ];

  // Background Animation Elements
  const BackgroundElements = () => (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      <motion.div
        animate={{ y: [0, -30, 0], opacity: [0.1, 0.2, 0.1] }}
        transition={{ duration: 8, repeat: Infinity }}
        className="absolute -top-40 -right-40 w-80 h-80 bg-yellow-500/20 rounded-full blur-3xl"
      />
      <motion.div
        animate={{ y: [0, 30, 0], opacity: [0.08, 0.15, 0.08] }}
        transition={{ duration: 10, repeat: Infinity }}
        className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500/15 rounded-full blur-3xl"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-yellow-500/5 pointer-events-none" />
    </div>
  );

  return (
    <div className="min-h-screen bg-white dark:bg-gray-950 transition-colors overflow-hidden">
      <BackgroundElements />

      {/* Hero Section */}
      <section className="relative pt-20 pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-yellow-50 via-white to-blue-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="inline-flex items-center gap-2 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 px-4 py-2 rounded-full mb-6 text-sm font-semibold"
            >
              <Sparkles className="w-4 h-4" />
              Plateforme N°1 de Recrutement en Algérie
            </motion.div>

            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-gray-900 dark:text-white leading-tight">
              Révolutionnez Votre{" "}
              <span className="bg-gradient-to-r from-yellow-500 to-yellow-600 bg-clip-text text-transparent">
                Recrutement
              </span>
            </h1>

            <p className="text-xl md:text-2xl mb-10 text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Tirez parti de solutions alimentées par l'IA pour un recrutement simplifié et efficace. Connectez-vous avec les meilleurs candidats grâce à notre plateforme innovante.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                onClick={() => navigate('/register')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white rounded-2xl font-semibold hover:from-yellow-600 hover:to-yellow-700 transition-all shadow-lg shadow-yellow-500/30 flex items-center justify-center gap-2"
              >
                Commencer à Recruter
                <ArrowRight className="w-5 h-5" />
              </motion.button>
              <motion.button
                onClick={() => navigate('/about')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 border-2 border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-2xl font-semibold hover:bg-gray-50 dark:hover:bg-gray-800 transition-all"
              >
                En Savoir Plus
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative py-20 bg-gray-50 dark:bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              Nos Solutions de Recrutement
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Découvrez notre suite complète de services conçus pour optimiser votre processus de recrutement
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 dark:border-gray-700 group"
              >
                <div className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${feature.color} flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                  {feature.icon}
                </div>

                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  {feature.title}
                </h3>

                <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
                  {feature.description}
                </p>

                <ul className="space-y-3">
                  {feature.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-center text-gray-700 dark:text-gray-300">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                      <span className="text-sm">{benefit}</span>
                    </li>
                  ))}
                </ul>

                <motion.button
                  whileHover={{ x: 5 }}
                  className="mt-6 text-yellow-600 dark:text-yellow-400 font-semibold flex items-center hover:text-yellow-700 dark:hover:text-yellow-300 transition-colors"
                >
                  En Savoir Plus
                  <ArrowRight className="w-4 h-4 ml-1" />
                </motion.button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      {/* <section className="relative py-20 bg-gradient-to-r from-yellow-500 to-yellow-600 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse delay-1000" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.5 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-white/20 text-white mb-4">
                  {stat.icon}
                </div>
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                  {stat.number}
                </div>
                <div className="text-yellow-100 text-sm md:text-base font-medium">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Testimonials Section */}
      {/* <section className="relative py-20 bg-white dark:bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              Ce Que Disent Nos Clients
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300">
              Témoignages d'entreprises qui ont transformé leur processus de recrutement
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gray-50 dark:bg-gray-900 rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-800"
              >
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>

                <p className="text-gray-700 dark:text-gray-300 mb-6 italic leading-relaxed">
                  "{testimonial.content}"
                </p>

                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {testimonial.name}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {testimonial.role}, {testimonial.company}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="relative py-20 bg-gradient-to-br from-gray-900 to-gray-800 dark:from-gray-950 dark:to-gray-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-yellow-500 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500 rounded-full blur-3xl animate-pulse delay-1000" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Prêt à Transformer Votre Recrutement ?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Rejoignez des milliers d'entreprises qui font confiance à Tekhdem pour leurs besoins de recrutement
            </p>
            <motion.button
              onClick={() => navigate('/register')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 bg-gradient-to-r from-yellow-500 to-yellow-600 text-white rounded-2xl font-semibold hover:from-yellow-600 hover:to-yellow-700 transition-all shadow-lg shadow-yellow-500/30 inline-flex items-center gap-2"
            >
              <Zap className="w-5 h-5" />
              Commencer Aujourd'hui
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;